import { Component } from "react";
import type { ReactNode } from "react";
import { Alert, Button, Code, Group, Paper, Stack, Text } from "@mantine/core";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** When true, render the error message + stack inline (useful for diagnostics in dev). */
  verbose?: boolean;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    // Always log to the browser console so the failure is greppable even when
    // we render the soft fallback. `componentStack` includes the React tree
    // path that bubbled into us, which is hugely helpful when the page has
    // many child components.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  override render() {
    if (this.state.error) {
      // Dev/diagnostic mode — surface the real error so we can read it
      // without opening the console. Stack is collapsed by default.
      if (this.props.verbose || import.meta.env.DEV) {
        const err = this.state.error;
        return (
          <Paper p="md" radius="md" withBorder>
            <Stack gap="xs">
              <Alert color="red" title="Something went wrong">
                <Text size="sm">{err.message || String(err)}</Text>
              </Alert>
              {err.stack && (
                <Code block style={{ maxHeight: 240, overflow: "auto", fontSize: 11 }}>
                  {err.stack}
                </Code>
              )}
              <Group>
                <Button size="xs" variant="light" onClick={() => this.setState({ error: null })}>
                  Retry
                </Button>
              </Group>
            </Stack>
          </Paper>
        );
      }
      // Production fallback — simple, friendly message.
      return (
        this.props.fallback ?? (
          <Paper p="md" radius="md" withBorder>
            <Text size="sm" c="dimmed">
              Something went wrong loading this section.
            </Text>
          </Paper>
        )
      );
    }
    return this.props.children;
  }
}
